"use client"

import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react"
import { Locale } from "@lib/data/locales"
import useToggleState from "@lib/hooks/use-toggle-state"
import { storefront } from "@lib/storefront"
import { BarsThree, ChevronDownMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Fragment } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"

type SideMenuProps = {
  categories?: HttpTypes.StoreProductCategory[]
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
}

const SideMenu = ({ categories = [], regions, locales, currentLocale }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  return (
    <Popover className="flex h-full items-center">
      {({ open, close }) => (
        <>
          <PopoverButton
            data-testid="nav-menu-button"
            className="flex h-11 w-11 items-center justify-center rounded-[var(--sf-radius-control)] transition-colors hover:bg-header-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-header-foreground"
            aria-label="Abrir menú de navegación"
          >
            <BarsThree className="h-6 w-6" aria-hidden="true" />
          </PopoverButton>

          {open && (
            <button
              type="button"
              className="fixed inset-0 z-50 cursor-default bg-black/45"
              onClick={close}
              aria-label="Cerrar menú de navegación"
              data-testid="side-menu-backdrop"
            />
          )}

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <PopoverPanel
              static
              className="fixed inset-y-0 left-0 z-[51] flex w-[min(88vw,24rem)] flex-col bg-[var(--sf-color-surface)] text-[var(--sf-color-foreground)] shadow-2xl"
              data-testid="nav-menu-popup"
            >
              <div className="flex min-h-16 items-center justify-between border-b border-header-border bg-header px-4 text-header-foreground">
                <span className="font-heading text-lg font-semibold uppercase">
                  {storefront.brand.shortName}
                </span>
                <button
                  type="button"
                  data-testid="close-menu-button"
                  onClick={close}
                  className="flex h-11 w-11 items-center justify-center rounded-[var(--sf-radius-control)] hover:bg-header-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-header-foreground"
                  aria-label="Cerrar menú"
                >
                  <XMark className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto overscroll-contain px-4 py-4" aria-label="Menú móvil">
                <ul className="space-y-1">
                  <li><MenuLink href="/" label="Inicio" close={close} /></li>
                  <li><MenuLink href="/store" label="Todos los productos" close={close} /></li>
                </ul>

                {categories.length > 0 && (
                  <section className="mt-6" aria-labelledby="mobile-categories-title">
                    <h2 id="mobile-categories-title" className="px-3 pb-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--sf-color-muted)]">
                      Comprar por categoría
                    </h2>
                    <div className="divide-y divide-[var(--sf-color-border)]">
                      {categories.map((category) => {
                        const children = category.category_children ?? []
                        const categoryHref = `/categories/${category.handle}`

                        if (children.length === 0) {
                          return <MenuLink key={category.id} href={categoryHref} label={category.name} close={close} />
                        }

                        return (
                          <details key={category.id} className="group">
                            <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 rounded-[var(--sf-radius-control)] px-3 py-3 text-sm font-semibold hover:bg-[var(--sf-color-background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sf-color-focus)]">
                              {category.name}
                              <ChevronDownMini className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden="true" />
                            </summary>
                            <ul className="mb-2 ml-3 border-l border-[var(--sf-color-border)] pl-2">
                              <li><MenuLink href={categoryHref} label={`Ver todo ${category.name}`} close={close} emphasis /></li>
                              {children.map((child) => (
                                <li key={child.id}>
                                  <MenuLink
                                    href={`${categoryHref}/${child.handle}`}
                                    label={child.name}
                                    close={close}
                                  />
                                </li>
                              ))}
                            </ul>
                          </details>
                        )
                      })}
                    </div>
                  </section>
                )}

                {storefront.header.showAccount && (
                  <div className="mt-6 border-t border-[var(--sf-color-border)] pt-4">
                    <MenuLink href="/account" label="Mi cuenta" close={close} emphasis />
                  </div>
                )}
              </nav>

              <div className="space-y-4 border-t border-[var(--sf-color-border)] px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 text-sm">
                {!!locales?.length && (
                  <LanguageSelect toggleState={languageToggleState} locales={locales} currentLocale={currentLocale} />
                )}
                {!!regions?.length && <CountrySelect toggleState={countryToggleState} regions={regions} />}
                <p className="text-xs text-[var(--sf-color-muted)]">
                  © {new Date().getFullYear()} {storefront.brand.name}
                </p>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

function MenuLink({
  href,
  label,
  close,
  emphasis = false,
}: {
  href: string
  label: string
  close: () => void
  emphasis?: boolean
}) {
  return (
    <LocalizedClientLink
      href={href}
      prefetch={false}
      onClick={close}
      className={`flex min-h-12 items-center rounded-[var(--sf-radius-control)] px-3 py-2 text-sm transition-colors hover:bg-[var(--sf-color-background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sf-color-focus)] ${emphasis ? "font-semibold text-[var(--sf-color-brand)]" : "font-medium"}`}
    >
      {label}
    </LocalizedClientLink>
  )
}

export default SideMenu
